import type { ReactNode } from 'react';
import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Tabs, Typography, theme } from 'antd';
import type { RankingDetailOut } from '@/api/generated/schemas';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { CoverThumb } from '@/components/common/show/CoverThumb';
import { RANKING_SHOW_TABS } from '../constants';
import type { RankingShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<RankingShowTab, string> = {
  general: 'base.sections.basic_info',
  publications: 'user_catalog.common.tabs.publications',
  collection: 'base.sections.collection_info',
  search: 'user_catalog.common.tabs.search',
};

interface RankingShowHeaderProps {
  record: RankingDetailOut;
  activeTab: RankingShowTab;
  onTabChange: (key: string) => void;
}

const StatCell = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => {
  const { token } = theme.useToken();
  return (
    <div>
      <div
        style={{
          fontSize: token.fontSizeSM,
          color: token.colorTextTertiary,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: token.fontSizeHeading5,
          lineHeight: token.lineHeightHeading5,
          marginTop: token.marginXXS,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export const RankingShowHeader = ({
  record,
  activeTab,
  onTabChange,
}: RankingShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <CoverThumb url={record.cover_url} size={128} />

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Long names wrap instead of pushing the tabs bar out. */}
          <Title
            level={3}
            style={{
              margin: 0,
              fontSize: token.fontSizeHeading3,
              overflowWrap: 'anywhere',
            }}
          >
            {record.name}
          </Title>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell
              label={t('user_catalog.rankings.fields.publications_count')}
            >
              {record.publication_rankings?.length ?? 0}
            </StatCell>
            <StatCell label={t('base.fields.mod_status')}>
              <ModStatusTag value={record.mod_status} />
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={RANKING_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
